import React, { useState } from 'react';
import { FaRegCopy, FaCheck } from 'react-icons/fa6';

import * as styled from '../styles/copy-profile-button';

type Props = {
  provider: Kudable.Provider;
  services: Kudable.Service[];
  token: string;
};

const FEEDBACK_DURATION_MS = 2000;
const ICON_SIZE_PX = 14;

const formatPrice = (
  min?: number | null,
  max?: number | null,
  note?: string | null,
): string => {
  if (min == null && max == null) return note ?? '';
  if (min != null && max != null && min !== max)
    return `${min.toLocaleString()} – ${max.toLocaleString()}`;
  const single = (min ?? max)!;
  const base = single.toLocaleString();
  return note ? `${base} (${note})` : base;
};

const buildProfileText = (
  provider: Kudable.Provider,
  services: Kudable.Service[],
  token: string,
): string => {
  const displayName =
    provider.company_mode && provider.business_name
      ? provider.business_name
      : `${provider.first_name ?? ''} ${provider.last_name ?? ''}`.trim();

  const titleLine = provider.title
    ? `${provider.title}${provider.workplace ? ` · ${provider.workplace}` : ''}`
    : provider.workplace ?? '';

  const lines: string[] = [];
  if (displayName) lines.push(displayName);
  if (titleLine) lines.push(titleLine);
  if (provider.tagline) lines.push(provider.tagline);
  lines.push('');

  if (services.length > 0) {
    lines.push('Services:');
    services.forEach((service) => {
      const price = formatPrice(
        service.price_min,
        service.price_max,
        service.price_note,
      );
      lines.push(
        price ? `• ${service.name} — ${price}` : `• ${service.name}`,
      );
    });
    lines.push('');
  }

  lines.push(`${window.location.origin}/p/${token}`);
  return lines.join('\n');
};

export const CopyProfileButton = ({ provider, services, token }: Props) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = buildProfileText(provider, services, token);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), FEEDBACK_DURATION_MS);
      })
      .catch(() => {
        // Clipboard write can fail in insecure contexts or older browsers; we
        // intentionally swallow the rejection — the user can still copy the URL
        // from the address bar.
      });
  };

  return (
    <styled.Wrap>
      <styled.Button type="button" onClick={handleCopy} $copied={copied}>
        {copied ? (
          <FaCheck size={ICON_SIZE_PX} />
        ) : (
          <FaRegCopy size={ICON_SIZE_PX} />
        )}
        <span>{copied ? 'Profile copied!' : 'Copy this profile'}</span>
      </styled.Button>
      <styled.Hint>
        Paste it into a message, or use it as a template for your own.
      </styled.Hint>
    </styled.Wrap>
  );
};
